import { Button } from "@/components/ui/button";
import { Check } from "lucide-react";
import { Link } from "react-router-dom";

export default function Hero() {
  const scrollToSection = (href: string) => {
    const element = document.querySelector(href);
    if (element) {
      element.scrollIntoView({ behavior: "smooth" });
    }
  };

  return (
    <section
      id="hero"
      className="relative min-h-screen flex items-center justify-center overflow-hidden bg-deep-navy"
    >
      {/* Animated Background Orbs - Pure cyan minimal */}
      <div className="absolute inset-0 overflow-hidden pointer-events-none">
        <div className="absolute top-20 left-10 w-96 h-96 bg-cyan/15 rounded-full blur-3xl float-animation" />
        <div className="absolute top-40 right-20 w-80 h-80 bg-cyan/10 rounded-full blur-3xl float-slow" />
        <div className="absolute bottom-20 right-40 w-72 h-72 bg-cyan/12 rounded-full blur-3xl float-animation" style={{ animationDelay: "2s" }} />
        <div className="absolute bottom-40 left-32 w-64 h-64 bg-cyan/8 rounded-full blur-3xl float-slow" style={{ animationDelay: "3s" }} />
      </div>

      {/* Hexagon Pattern Overlay */}
      <div className="absolute inset-0 opacity-5 pointer-events-none">
        <svg width="100%" height="100%" xmlns="http://www.w3.org/2000/svg">
          <defs>
            <pattern id="hexagons" x="0" y="0" width="50" height="43.4" patternUnits="userSpaceOnUse">
              <polygon points="24.8,22 37.3,29.2 37.3,43.7 24.8,50.9 12.3,43.7 12.3,29.2" fill="none" stroke="currentColor" strokeWidth="0.5" />
            </pattern>
          </defs>
          <rect width="100%" height="100%" fill="url(#hexagons)" />
        </svg>
      </div>

      {/* Content */}
      <div className="relative z-10 max-w-5xl mx-auto px-6 text-center fade-in-up">
        {/* Badge */}
        <div className="inline-flex items-center gap-2 px-4 py-2 rounded-full border border-cyan/30 bg-cyan/5 mb-8 text-sm text-cyan font-medium">
          AI Transformation Partners
        </div>

        {/* Headline */}
        <h1 className="text-5xl md:text-7xl font-bold leading-tight mb-6 max-w-4xl mx-auto">
          <span className="text-foreground">We're Not Just an Automation Agency.</span>
          <br />
          <span className="text-foreground">We're </span>
          <span className="relative inline-block">
            <span className="text-cyan">AI Transformation Partners.</span>
            <div className="absolute -bottom-2 left-0 w-full h-1 bg-gradient-to-r from-cyan via-cyan/50 to-transparent rounded-full"></div>
          </span>
        </h1>

        {/* Subheading */}
        <p className="text-xl md:text-2xl text-light-gray/95 leading-relaxed mb-4 max-w-3xl mx-auto">
          We help businesses identify, implement, and scale AI systems that think, learn, and grow with you.
        </p>
        
        {/* Supporting Line */}
        <p className="text-lg md:text-xl text-cyan/90 font-medium mb-10 max-w-3xl mx-auto">
          From strategy to systems — we guide your transformation every step of the way.
        </p>

        {/* CTAs */}
        <div className="flex flex-col sm:flex-row gap-4 justify-center mb-10">
          <Button
            asChild
            size="lg"
            className="bg-cyan text-deep-navy hover:bg-cyan/90 hover:scale-105 transition-all duration-300 shadow-lg hover:shadow-cyan/50 hover:shadow-2xl px-8 py-6 text-lg font-bold"
          >
            <a
              href="https://calendly.com/arqeraai-strategy-call/"
              target="_blank"
              rel="noopener noreferrer"
            >
              Book Your AI Strategy Audit
            </a>
          </Button>
          <Button
            asChild
            size="lg"
            variant="outline"
            className="border-2 border-bronze/50 text-bronze hover:bg-bronze/10 hover:border-bronze hover:scale-105 transition-all duration-300 px-8 py-6 text-lg"
          >
            <Link to="/ai-transformation-audit">
              Take the AI Transformation Audit
            </Link>
          </Button>
        </div>

        {/* Trust Indicators */}
        <div className="flex flex-wrap items-center justify-center gap-6 text-sm text-medium-gray">
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-cyan" />
            <span>5-minute call (not a sales pitch)</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-cyan" />
            <span>Custom analysis included</span>
          </div>
          <div className="flex items-center gap-2">
            <Check className="w-4 h-4 text-cyan" />
            <span>Zero commitment</span>
          </div>
        </div>
      </div>
    </section>
  );
}
